import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { groupsSelectors } from '@/entities/groups'
import { Group } from '@/entities/groups/models/Group'
import { logoutUser } from '@/entities/user'
import { setBusinessModel } from '@/entities/user/userSlice'
import { updateActiveGroup } from '@/entities/userInterface'
import { NavItem } from '@/fragments/NavSidebar/NavItem'
import { NavSidebar } from '@/fragments/NavSidebar/NavSidebar'
import { CustomIcon } from '@/shared/components/Icon/CustomIcon'
import { IconButton } from '@/shared/components/IconButton/IconButton'
import { useSubscription } from '@/shared/components/Subscription/SubscriptionContext'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faFileImport, faGrid2, faUserShield } from '@fortawesome/pro-regular-svg-icons'
import { Grid, styled, useTheme } from '@mui/material'

import { useColorModeContext } from '../providers/GlobalThemeProvider/GlobalThemeProvider'
import { useLayoutContext } from './LayoutContext'
import { ProfileDropdown } from './ProfileDropdown'

export type NavigationProps = {
  //
}

// Main Navigation Component
export const Navigation: React.FC<NavigationProps> = () => {
  const isDarkTheme = useTheme().palette.mode === 'dark'
  const activeGroupId = useAppSelector(state => state.userInterface.activeGroupId)
  const user = useAppSelector(state => state.user)
  const { subscription } = useSubscription()
  const groups = useAppSelector(state => groupsSelectors.selectAll(state))
  const { isNavOpen, setNavOpen } = useLayoutContext()
  const { toggleColorMode } = useColorModeContext()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const activeGroup: Group | undefined = useMemo(() =>
    groups.find(group => group.id === activeGroupId) ?? groups[0],
  [groups, activeGroupId],
  )

  const onNavItemSelect = () => {
    setNavOpen(false)
  }

  const onGroupSelect = (value: string) => {
    dispatch(updateActiveGroup({
      groupId: parseInt(value),
    }))
  }

  const onToggleCollapse = () => {
    setNavOpen((prev) => !prev)
  }

  const onUpgradeSubscription = () => {
    navigate('/upgrade', {
      state: { from: location },
    })
  }

  const onManageSubscription = () => {
    navigate('/subscription/manage')
  }

  const logout = () => {
    dispatch(logoutUser())
      .then(() => {
        navigate('/', {
          state: { from: location },
        })
      })
  }

  // Side Effect to update business model whenever the group change or initial load
  useEffect(() => {
    if (!activeGroup || groups.length === 0) return

    const groupBusinessModel = activeGroup?.businessModel

    if (groupBusinessModel && groupBusinessModel > 0 && groupBusinessModel !== user.businessModel) {
      dispatch(setBusinessModel(groupBusinessModel))
    }
  }, [groups, activeGroup, user.businessModel, dispatch])

  return (
    <NavSidebar open={isNavOpen} logoHref="/operations">
      {/* Enhanced Profile Section - Now at the top replacing group selector */}
      <ProfileDropdown
        user={user}
        activeGroup={activeGroup}
        groups={groups}
        subscription={subscription}
        isNavOpen={isNavOpen}
        onGroupSelect={onGroupSelect}
        onManageSubscription={onManageSubscription}
        onUpgradeSubscription={onUpgradeSubscription}
        toggleColorMode={toggleColorMode}
        logout={logout}
        isDarkTheme={isDarkTheme}
      />

      {/* Navigation Items */}
      <NavItem href="/operations" id="primary-nav-operations" onClick={onNavItemSelect} icon={faGrid2} isNavOpen={isNavOpen}>
        Operations
      </NavItem>
      <NavItem href="/import" id="primary-nav-import" onClick={onNavItemSelect} icon={faFileImport} isNavOpen={isNavOpen}>
        Import Wizard
      </NavItem>
      <NavItem href="/admin" id="primary-nav-admin" onClick={onNavItemSelect} icon={faUserShield} isNavOpen={isNavOpen}>
        Admin
      </NavItem>

      <StyledAdditionalNavActions>
        <Grid container direction={'row'} justifyContent={'flex-end'}>
          <Grid item>
            <StyledIconButton
              dark
              round={false}
              onClick={onToggleCollapse}
              tooltipTitle="Toggle Sidebar Open"
            >
              <CustomIcon svg iconCode={isNavOpen ? 'leftSidebarCollapse' : 'leftSidebarExpand'} width={pxToRem(16)} />
            </StyledIconButton>
          </Grid>
        </Grid>
      </StyledAdditionalNavActions>
    </NavSidebar>
  )
}

const StyledAdditionalNavActions = styled('div')`
  margin-top: auto;
  margin-bottom: ${pxToRem(24)};
`

const StyledIconButton = styled(IconButton)`
  color: ${(props) => props.theme.palette.custom.sidebar.textDimColor};
  font-size: ${pxToRem(16)};
  margin: 0.4375rem 0.75rem;
`

